import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "./utils/api";
import { QUICK_DATE_RANGES, quickDateRange } from "./utils/indiaTime";
import "./InsightsView.css";

// Results > Insights: opens, unique people, submissions, completion rate,
// average time to complete, a daily chart of opens and submissions, and the
// page drop-off, for a date range (India dates; default the last 30 days).
// All numbers come from the server (owner only).

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "June", "July", "Aug", "Sept", "Oct", "Nov", "Dec"];
const dayLabel = (ymd, withYear = false) => {
    const [y, m, d] = ymd.split("-").map(Number);
    return `${d} ${MONTHS[m - 1]}${withYear ? ` ${y}` : ""}`;
};
const people = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// ---- daily chart -------------------------------------------------------------

const SERIES = [
    { key: "opens", label: "Opens", color: "var(--insights-series-1)" },
    { key: "submissions", label: "Submissions", color: "var(--insights-series-2)" },
];
// Drawn at its on-screen width (measured), so text stays at its real size.
const H = 250, M = { top: 14, right: 104, bottom: 28, left: 40 };

function niceMax(v) {
    if (v <= 4) return 4;
    const step = Math.pow(10, Math.floor(Math.log10(v)));
    for (const k of [1, 2, 2.5, 5, 10]) if (k * step * 4 >= v) return k * step * 4;
    return v;
}

function DailyChart({ days }) {
    const [hover, setHover] = useState(null);
    const [asTable, setAsTable] = useState(false);
    const wrapRef = useRef(null);
    const [W, setW] = useState(720);
    useEffect(() => {
        const el = wrapRef.current;
        if (!el || typeof ResizeObserver === "undefined") return undefined;
        const ro = new ResizeObserver(entries => {
            const width = Math.round(entries[0].contentRect.width);
            if (width > 0) setW(Math.max(320, width));
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [asTable]);
    const max = niceMax(Math.max(1, ...days.map(d => Math.max(d.opens, d.submissions))));
    const innerW = W - M.left - M.right, innerH = H - M.top - M.bottom;
    const x = (i) => M.left + (days.length === 1 ? innerW / 2 : (i * innerW) / (days.length - 1));
    const y = (v) => M.top + innerH - (v / max) * innerH;
    const ticks = [0, 1, 2, 3, 4].map(i => (max / 4) * i);
    const step = Math.max(1, Math.ceil(days.length / Math.max(2, Math.floor(innerW / 90))));
    const labelIdx = days.map((_, i) => i).filter(i => i % step === 0 || i === days.length - 1).filter((i, k, arr) => !(k === arr.length - 2 && arr[k + 1] - i < step / 2));
    const path = (key) => days.map((d, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(d[key]).toFixed(1)}`).join(" ");

    // End labels: in text ink, nudged apart if the two lines end close together.
    const last = days[days.length - 1];
    let ends = SERIES.map(s => ({ ...s, y: y(last[s.key]) }));
    if (Math.abs(ends[0].y - ends[1].y) < 14) {
        const mid = (ends[0].y + ends[1].y) / 2;
        const upper = ends[0].y <= ends[1].y ? 0 : 1;
        ends = ends.map((e, i) => ({ ...e, y: i === upper ? mid - 8 : mid + 8 }));
    }

    const onMove = (e) => {
        const svg = e.currentTarget.ownerSVGElement || e.currentTarget;
        const rect = svg.getBoundingClientRect();
        const px = ((e.clientX - rect.left) / rect.width) * W;
        let i = Math.round(((px - M.left) / innerW) * (days.length - 1));
        i = Math.max(0, Math.min(days.length - 1, i));
        setHover(i);
    };

    if (asTable) {
        return (
            <div>
                <div className="insights-chart-head">
                    <h4>Opens and submissions per day</h4>
                    <button type="button" className="insights-link" onClick={() => setAsTable(false)}>Show chart</button>
                </div>
                <div className="insights-table-wrap">
                    <table className="insights-table">
                        <thead><tr><th>Date</th><th>Opens</th><th>Submissions</th></tr></thead>
                        <tbody>{days.map(d => <tr key={d.date}><td>{dayLabel(d.date, true)}</td><td>{d.opens}</td><td>{d.submissions}</td></tr>)}</tbody>
                    </table>
                </div>
            </div>
        );
    }

    const tip = hover === null ? null : days[hover];
    const tipLeftPct = hover === null ? 0 : (x(hover) / W) * 100;
    return (
        <div>
            <div className="insights-chart-head">
                <h4>Opens and submissions per day</h4>
                <div className="insights-legend" aria-hidden="true">
                    {SERIES.map(s => <span key={s.key}><i style={{ background: s.color }}></i>{s.label}</span>)}
                </div>
                <button type="button" className="insights-link" onClick={() => setAsTable(true)}>Show as table</button>
            </div>
            <div className="insights-chart" ref={wrapRef}>
                <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label={`Opens and submissions per day, ${dayLabel(days[0].date, true)} to ${dayLabel(last.date, true)}`}>
                    {ticks.map(t => (
                        <g key={t}>
                            <line x1={M.left} x2={M.left + innerW} y1={y(t)} y2={y(t)} className="insights-grid" />
                            <text x={M.left - 8} y={y(t)} className="insights-axis" textAnchor="end" dominantBaseline="middle">{Number.isInteger(t) ? t : t.toFixed(1)}</text>
                        </g>
                    ))}
                    {labelIdx.map(i => (
                        <text key={i} x={x(i)} y={H - 8} className="insights-axis" textAnchor="middle">{dayLabel(days[i].date)}</text>
                    ))}
                    {hover !== null && <line x1={x(hover)} x2={x(hover)} y1={M.top} y2={M.top + innerH} className="insights-crosshair" />}
                    {SERIES.map(s => (
                        <path key={s.key} d={path(s.key)} fill="none" stroke={s.color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                    ))}
                    {hover !== null && SERIES.map(s => (
                        <circle key={s.key} cx={x(hover)} cy={y(days[hover][s.key])} r="4" fill={s.color} className="insights-dot" />
                    ))}
                    {ends.map(e => (
                        <g key={e.key}>
                            <line x1={M.left + innerW + 6} x2={M.left + innerW + 16} y1={e.y} y2={e.y} stroke={e.color} strokeWidth="2" strokeLinecap="round" />
                            <text x={M.left + innerW + 20} y={e.y} className="insights-end-label" dominantBaseline="middle">{e.label}</text>
                        </g>
                    ))}
                    <rect x={M.left - 6} y={M.top} width={innerW + 12} height={innerH} fill="transparent"
                        onMouseMove={onMove} onMouseLeave={() => setHover(null)} />
                </svg>
                {tip && (
                    <div className="insights-tip" style={{ left: `${tipLeftPct}%`, transform: tipLeftPct > 60 ? "translateX(calc(-100% - 12px))" : "translateX(12px)" }} role="status">
                        <strong>{dayLabel(tip.date, true)}</strong>
                        {SERIES.map(s => <span key={s.key}><i style={{ background: s.color }}></i>{s.label}: {tip[s.key]}</span>)}
                    </div>
                )}
            </div>
        </div>
    );
}

// ---- page drop-off -------------------------------------------------------------

function DropOff({ pages }) {
    const top = Math.max(1, ...pages.map(p => p.sessions));
    return (
        <div className="insights-dropoff">
            <h4>Page drop-off</h4>
            <p className="insights-muted">Fill sessions that reached each page, and the share lost since the previous page.</p>
            <ol>
                {pages.map((p, i) => (
                    <li key={p.page_id}>
                        <span className="insights-page-name">{i + 1}. {p.title}</span>
                        <span className="insights-bar-track"><span className="insights-bar" style={{ width: `${(p.sessions / top) * 100}%` }}></span></span>
                        <span className="insights-page-num">{people(p.sessions, "session", "sessions")}</span>
                        <span className={`insights-page-lost${p.lost_pct ? " insights-lost" : ""}`}>
                            {p.lost_pct === null ? (i === 0 ? "First page" : "-") : `${p.lost_pct}% lost`}
                        </span>
                    </li>
                ))}
            </ol>
        </div>
    );
}

// ---- who opened ------------------------------------------------------------------

function WhoOpened({ formId, range, anonymous, cardRef }) {
    const [list, setList] = useState(null);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");

    useEffect(() => {
        if (anonymous) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const res = await apiFetch(`/api/insights/${formId}/people?from=${range.from}&to=${range.to}`, { method: "GET" });
                const body = await res.json();
                if (!res.ok) throw new Error(body.error || "Could not load who opened the form.");
                if (!cancelled) { setList(body.people); setError(null); }
            } catch (err) {
                if (!cancelled) setError(err.message);
            }
        })();
        return () => { cancelled = true; };
    }, [formId, range.from, range.to, anonymous]);

    const term = search.trim().toLowerCase();
    const shown = (list || []).filter(p => !term || [p.name, p.emp_id, p.department, p.location].some(v => v && v.toLowerCase().includes(term)));

    return (
        <div className="insights-card insights-who" ref={cardRef}>
            <div className="insights-chart-head">
                <h4>Who opened</h4>
                {list && list.length > 0 && (
                    <input type="search" className="insights-who-search" placeholder="Search name, id, department"
                        aria-label="Search who opened" value={search} onChange={(e) => setSearch(e.target.value)} />
                )}
            </div>
            {anonymous ? (
                <p className="insights-muted">This form is anonymous: who opened it is not recorded, only the counts.</p>
            ) : error ? (
                <p className="insights-error">{error}</p>
            ) : !list ? (
                <p className="insights-muted">Loading...</p>
            ) : list.length === 0 ? (
                <p className="insights-muted">Nobody has opened the form in this date range.</p>
            ) : (
                <div className="insights-table-wrap insights-who-wrap">
                    <table className="insights-table insights-who-table">
                        <thead>
                            <tr>
                                <th>Employee</th><th>Emp ID</th><th>Department</th><th>Location</th><th>Opens</th>
                                <th>First opened</th><th>Last activity</th><th>Furthest page</th><th>Submitted</th>
                            </tr>
                        </thead>
                        <tbody>
                            {shown.map(p => (
                                <tr key={p.emp_id}>
                                    <td className={p.active ? "" : "insights-muted"}>{p.name}</td>
                                    <td>{p.emp_id}</td>
                                    <td>{p.department || "-"}</td>
                                    <td>{p.location || "-"}</td>
                                    <td>{p.opens}</td>
                                    <td>{p.first_opened || "-"}</td>
                                    <td>{p.last_activity || "-"}</td>
                                    <td>{p.furthest_page || "-"}</td>
                                    <td><span className={`insights-badge ${p.submitted ? "insights-badge-yes" : "insights-badge-no"}`}>{p.submitted ? "Submitted" : "Not yet"}</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {shown.length === 0 && <p className="insights-muted insights-pad">No one matches "{search}".</p>}
                </div>
            )}
        </div>
    );
}

// ---- the view -----------------------------------------------------------------

export default function InsightsView({ formId }) {
    const [range, setRange] = useState(null); // null: the server's default (last 30 days)
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState({ from: "", to: "" });
    const whoRef = useRef(null);
    const seeWho = () => whoRef.current && whoRef.current.scrollIntoView({ behavior: "smooth", block: "start" });

    const load = useCallback(async () => {
        try {
            const qs = range ? `?from=${range.from}&to=${range.to}` : "";
            const res = await apiFetch(`/api/insights/${formId}${qs}`, { method: "GET" });
            const body = await res.json();
            if (!res.ok) throw new Error(body.error || "Could not load insights.");
            setData(body);
            setDraft(body.range);
            setError(null);
        } catch (err) {
            setError(err.message);
        }
    }, [formId, range]);

    useEffect(() => { if (formId) load(); }, [formId, load]);

    const apply = (next) => { setRange(next); setOpen(false); };
    const tiles = useMemo(() => {
        if (!data) return [];
        return [
            { label: "Opens", value: data.opens, note: people(data.sessions, "fill session", "fill sessions"), who: !data.anonymous && data.opens > 0 },
            { label: "Unique people who opened", value: data.unique_people === null ? "Hidden" : data.unique_people, note: data.unique_people === null ? "Not recorded on anonymous forms" : "Signed-in people", who: !data.anonymous && data.unique_people > 0 },
            { label: "Submissions", value: data.submissions, note: "Counted from fill sessions" },
            { label: "Completion rate", value: data.completion_rate === null ? "-" : `${data.completion_rate}%`, note: "Submissions / fill sessions" },
            { label: "Average time to complete", value: data.avg_complete_text || "-", note: "From opening to submitting" },
        ];
    }, [data]);

    if (error) return <p className="insights-error">{error}</p>;
    if (!data) return <p className="insights-muted insights-pad">Loading insights...</p>;

    return (
        <div className="insights-view">
            <div className="insights-filter">
                <div className="insights-range">
                    <button type="button" className="insights-range-btn" onClick={() => setOpen(o => !o)} aria-expanded={open}>
                        <i className="fa-solid fa-calendar-days"></i> Date Range
                    </button>
                    <span className="insights-range-text">{dayLabel(data.range.from, true)} - {dayLabel(data.range.to, true)}</span>
                    {open && (
                        <div className="insights-range-pop" role="dialog" aria-label="Date range">
                            <div className="insights-range-inputs">
                                <input type="date" aria-label="From" value={draft.from} onChange={(e) => setDraft(d => ({ ...d, from: e.target.value }))} />
                                <span>to</span>
                                <input type="date" aria-label="To" value={draft.to} onChange={(e) => setDraft(d => ({ ...d, to: e.target.value }))} />
                            </div>
                            <div className="insights-range-quick">
                                {QUICK_DATE_RANGES.map(r => (
                                    <button type="button" key={r.days} onClick={() => apply(quickDateRange(r.days))}>{r.label}</button>
                                ))}
                            </div>
                            <div className="insights-range-actions">
                                <button type="button" className="insights-link" onClick={() => apply(null)}>Last 30 days</button>
                                <button type="button" className="insights-apply" disabled={!draft.from || !draft.to} onClick={() => apply(draft)}>Apply</button>
                            </div>
                        </div>
                    )}
                </div>
                <p className="insights-muted">Your own visits as the form's owner are not counted.</p>
            </div>

            <div className="insights-tiles">
                {tiles.map(t => (
                    <div className="insights-tile" key={t.label}>
                        <span className="insights-tile-label">{t.label}</span>
                        <span className="insights-tile-value">{t.value}</span>
                        <span className="insights-tile-note">{t.note}</span>
                        {t.who && <button type="button" className="insights-link insights-tile-who" onClick={seeWho}>See who</button>}
                    </div>
                ))}
            </div>

            {data.opens === 0 && data.submissions === 0
                ? <p className="insights-empty">No visits in this date range yet.</p>
                : (
                    <>
                        <WhoOpened formId={formId} range={data.range} anonymous={data.anonymous} cardRef={whoRef} />
                        <div className="insights-card"><DailyChart days={data.daily} /></div>
                        <div className="insights-card"><DropOff pages={data.pages} /></div>
                    </>
                )}
        </div>
    );
}
